/**
 * @file: useOcrImageCapture.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CameraViewLayout } from '@euler/app/components/media/camera/CameraView.shared';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { DeviceOrientationType } from '@euler/functions/useDeviceOrientationSensor';
import { useCallback, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';

export const useOcrImageCapture = (props: {
  mode: 'plate' | 'vin';
  autoRotate?: boolean;
  isMaskRotated?: boolean;
}) => {
  const { mode } = props;
  const { rpx } = useResponsiveContext();
  const { width, height } = useWindowDimensions();
  const isMobile = useIsMobileLayout();
  const isTablet = !isMobile;

  const deviceOrientation: DeviceOrientationType =
    width > height ? 'landscapeRight' : 'portrait';

  const [cameraLayout, setCameraLayout] = useState<CameraViewLayout>();

  const onCameraLayoutChange = useCallback((layout: CameraViewLayout) => {
    setCameraLayout(layout);
  }, []);

  const { preferredMaskSize, maskSize } = useMemo(() => {
    const w = Math.min(width, height);

    const preferredSize = {
      width: Math.round((isTablet ? Math.min(w, rpx(675)) : w) - rpx(30)),
      height: Math.round(
        rpx(
          isTablet
            ? mode === 'plate'
              ? 240
              : 200
            : mode === 'plate'
            ? 140
            : 100,
        ),
      ),
    };

    const size = {
      width: preferredSize.width,
      height: preferredSize.height,
    };

    if (
      (size.width > size.height && deviceOrientation !== 'portrait') ||
      (size.width < size.height && deviceOrientation === 'portrait')
    ) {
      // rotate the mask if they do not match.
      [size.width, size.height] = [size.height, size.width];
    }

    return { preferredMaskSize: preferredSize, maskSize: size };
  }, [deviceOrientation, height, isTablet, mode, rpx, width]);

  return {
    preferredMaskSize,
    maskSize,
    cameraLayout,
    screenOrientationInfo: undefined,
    deviceOrientation,
    onCameraLayoutChange,
    dimens: { width, height },
  };
};
